import { FC, Reducer, useEffect, useReducer, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, isEqual, isNumber, omit } from 'lodash';
import queryString from 'query-string';

import { SearchContext, SearchContextProps } from '@app/contexts';
import {
  useInstanceDetailsRoutePath,
  usePaging,
  useQueryParams,
} from '@app/hooks';
import {
  DateRange,
  generateRouteParams,
  getSafeInteger,
  removeEmptyKeys,
} from '@app/utils';
import {
  defaultRecordsPerPage,
  Route,
  SortDirection,
  URLQuery,
} from '@app/constants';

import {
  ActiveFacetsType,
  facetsOrder,
  SearchInstanceDto,
  SearchOption,
  SearchOptionType,
  SearchResultsDto,
  SearchResultsErrorsDto,
  SearchState,
  SortField,
} from '../constants';
import { useSearchRequest, useUrlManager } from '../hooks';
import { formatSelectedFacets } from '../utils';

export type SearchStateInitialized = SearchState &
  Required<
    Pick<
      SearchState,
      'isInnerUpdate' | 'query' | 'pageNumber' | 'recordsPerPage'
    >
  >;

type SearchProviderProps = {
  searchContext?: Partial<SearchContextProps>;
};

export const SearchProvider: FC<SearchProviderProps> = ({
  children,
  searchContext,
}) => {
  const [searchOption, setSearchOption] = useState<SearchOptionType>(
    SearchOption.keyword
  );
  const [selectedFacets, setSelectedFacets] = useState<ActiveFacetsType>({});
  const [selectedPublicationYear, setSelectedPublicationYear] =
    useState<DateRange>({});
  const [instanceIdScrollTo, setInstanceIdScrollTo] = useState(null);
  const [isPageFromBackButton, setIsPageFromBackButton] = useState(false);
  const totalRecordsRef = useRef(0);

  const initialSearchState = {
    pageNumber: 1,
    query: '',
    sortDirection: SortDirection.asc,
    sortField: SortField.relevance,
    option: SearchOption.keyword,
    facets: {},
    publicationYear: {},
    isInnerUpdate: true,
    isNewPageRequest: false,
    recordsPerPage: defaultRecordsPerPage,
  };

  const [state, updateState] = useReducer<
    Reducer<SearchStateInitialized, Partial<SearchState>>
  >(
    (prevState, newState) => ({
      ...prevState,
      ...newState,
      pageNumber: newState.pageNumber || 1,
      isInnerUpdate: Boolean(newState.isInnerUpdate),
      isNewPageRequest: Boolean(newState.isNewPageRequest),
    }),
    initialSearchState
  );
  const [isStatePristine, setIsStatePristine] = useState(true);
  const { getSearchStateFromUrl } = useUrlManager();
  const { pathname, search: urlSearchQuery } = useLocation();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const isMountedRef = useRef(false);
  const searchQueryRef = useRef('');
  const shouldRefocusRef = useRef(false);
  const { checkIsInstanceDetailsRoute } = useInstanceDetailsRoutePath();
  const isSearchRoute = pathname === Route.search;

  const [searchResults, setSearchResults] = useState<SearchInstanceDto[][]>([]);
  const fetchSearch = useSearchRequest(state);
  const searchResultsQuery = useQuery<SearchResultsDto, SearchResultsErrorsDto>(
    ['searchResults', omit(state, 'isInnerUpdate', 'isNewPageRequest')],
    fetchSearch,
    {
      cacheTime: 0,
      enabled: state.isNewPageRequest
        ? isEmpty(searchResults[state.pageNumber - 1])
        : !isStatePristine &&
          (Boolean(state.query) ||
            !isEmpty(state.facets) ||
            !isEmpty(state.publicationYear)),
    }
  );

  useEffect(() => {
    const newTotalRecords = searchResultsQuery.data?.totalRecords;
    const isValidTotalRecords = isNumber(newTotalRecords);

    if (isValidTotalRecords) {
      totalRecordsRef.current = newTotalRecords;
    }

    if (isValidTotalRecords && newTotalRecords === 1 && isSearchRoute) {
      navigate(
        `${generatePath(Route.instanceDetails, {
          id: searchResultsQuery.data?.instances?.[0].id as string,
        })}${urlSearchQuery}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsQuery.data?.totalRecords]);

  useEffect(() => {
    setSearchOption(state.option);
  }, [state.option]);

  useEffect(() => {
    const isQueryUpdatedFromSearchBar = state.query && !state.isInnerUpdate;
    const isSearchFromSearchResults =
      isSearchRoute &&
      (!isEmpty(state.facets) ||
        !isEmpty(state.publicationYear) ||
        state.query);

    // isMounted makes sure that useQuery doesn't fire before the actual changes to search state were made.
    if (isMountedRef.current) {
      if (
        isStatePristine &&
        (isSearchFromSearchResults || isQueryUpdatedFromSearchBar)
      ) {
        setIsStatePristine(false);
      }

      if (
        !isStatePristine &&
        isQueryUpdatedFromSearchBar &&
        searchQueryRef.current === state.query
      ) {
        searchResultsQuery.refetch();
      }

      searchQueryRef.current = state.query;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatePristine, state]);

  useEffect(() => {
    // Reset isInnerUpdate after every search fetch to avoid re-fetching results after navigating away from search results page
    updateState({
      isInnerUpdate: true,
      pageNumber: state.pageNumber,
      isNewPageRequest: state.isNewPageRequest,
    });

    // eslint-disable-next-line
  }, [searchResultsQuery.dataUpdatedAt, searchResultsQuery.errorUpdatedAt]);

  const syncUrlSelectedFacets = updatedFacets => {
    setSelectedFacets(() =>
      facetsOrder.reduce((result, facetKey) => {
        const selectedFacetsForKey = updatedFacets[facetKey]?.reduce(
          (newSelectedFacets, currentFacet) => {
            newSelectedFacets[currentFacet] = true;

            return newSelectedFacets;
          },
          {}
        );

        result[facetKey] = { ...selectedFacetsForKey };

        return result;
      }, {})
    );
  };

  const updateSearchStateFromUrl = (
    locationSearchQuery?: queryString.ParsedQuery
  ) => {
    const {
      query: updatedQuery = '',
      option: updatedOption = SearchOption.keyword,
      sortField: updatedSortField = SortField.relevance,
      sortDirection: updatedSortDirection = SortDirection.asc,
      facets: updatedFacets = {},
      publicationYear: updatedPublicationYear = {},
      pageNumber: updatedPageNumber = 1,
      recordsPerPage: updatedRecordsPerPage = defaultRecordsPerPage,
    } = getSearchStateFromUrl(locationSearchQuery);

    const newState = {
      query: updatedQuery,
      facets: updatedFacets,
      isInnerUpdate: true,
      isNewPageRequest: state.isNewPageRequest,
      publicationYear: updatedPublicationYear,
    };

    syncUrlSelectedFacets(updatedFacets);
    setSearchOption(updatedOption);
    setSelectedPublicationYear(
      isEmpty(updatedPublicationYear)
        ? updatedPublicationYear
        : {
            minDate: updatedPublicationYear.minDate ?? '',
            maxDate: updatedPublicationYear.maxDate ?? '',
          }
    );

    if (
      updatedQuery ||
      !isEmpty(updatedFacets) ||
      !isEmpty(updatedPublicationYear)
    ) {
      Object.assign(newState, {
        option: updatedOption,
        sortField: updatedSortField,
        sortDirection: updatedSortDirection,
        pageNumber: Number(updatedPageNumber),
        recordsPerPage: Number(updatedRecordsPerPage),
      });
      // update the url to remove parameters: option, sortField and sortDirection after page reload
      // as search request depends only on query and facets parameters in search state
    } else if (isSearchRoute) {
      navigate(
        generateRouteParams({
          pathname: Route.search,
          query: {},
        })
      );
    }

    updateState(newState);
    searchQueryRef.current = updatedQuery;
  };

  useEffect(() => {
    isMountedRef.current = true;

    updateSearchStateFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (pathname === Route.search || checkIsInstanceDetailsRoute(pathname)) &&
      state.isInnerUpdate
    ) {
      if (urlSearchQuery && urlSearchQuery !== '?') {
        updateSearchStateFromUrl(queryString.parse(urlSearchQuery));
      } else {
        syncUrlSelectedFacets({});
        setSelectedPublicationYear({});
        setSearchOption(SearchOption.keyword);
        updateState({ ...initialSearchState, isInnerUpdate: true });
        setSearchResults([]);
        totalRecordsRef.current = 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchQuery, pathname]);

  usePaging<SearchInstanceDto>({
    areRecordsFetched: searchResultsQuery.isSuccess,
    pageNumber: state.pageNumber,
    isNewPageRequest: Boolean(state.isNewPageRequest),
    records: searchResultsQuery.data?.instances,
    setPages: setSearchResults,
  });

  const updateFacets = (
    updatedFacets: Record<string, string | string[]>,
    key: 'facets' | 'publicationYear'
  ) => {
    const urlState = getSearchStateFromUrl();
    const wereFacetsUpdated = !isEqual(
      removeEmptyKeys(updatedFacets),
      state[key] ?? {}
    );

    if (wereFacetsUpdated && isMountedRef.current) {
      totalRecordsRef.current = 0;

      // remove empty facets from the URL if last facet was unchecked manually
      if (isEmpty(updatedFacets) && queryParams[key]) {
        let updatedQueryParams = queryParams;

        if (!queryParams.query) {
          updatedQueryParams = {
            ...omit(
              updatedQueryParams,
              'option',
              'pageNumber',
              'recordsPerPage'
            ),
          };
        }

        updatedQueryParams = {
          ...omit(updatedQueryParams, key),
        };

        navigate(
          generateRouteParams({
            pathname: Route.search,
            query: updatedQueryParams as URLQuery,
          })
        );
      } else {
        updateState({
          [key]: updatedFacets,
          option: searchOption,
          isInnerUpdate: isEqual(urlState[key] ?? {}, updatedFacets),
        });
      }
    }
  };

  useEffect(() => {
    const formattedFacets = formatSelectedFacets(selectedFacets);

    updateFacets(formattedFacets, 'facets');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacets, state.facets, updateState]);

  useEffect(() => {
    const { maxDate, minDate } = selectedPublicationYear;
    const sanitizedSelectedPublicationYear = {
      ...(maxDate && { maxDate }),
      ...(minDate && { minDate }),
    };

    updateFacets(sanitizedSelectedPublicationYear, 'publicationYear');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPublicationYear, state.publicationYear, updateState]);

  useEffect(() => {
    setSearchResults([]);
    totalRecordsRef.current = 0;
  }, [state.query]);

  return (
    <SearchContext.Provider
      value={{
        state,
        searchOption,
        searchResults: {
          searchResultsQuery,
          currentPage: searchResults[state.pageNumber - 1] || [],
          totalRecords: getSafeInteger(
            searchResultsQuery.data?.totalRecords,
            totalRecordsRef.current
          ),
          pages: searchResults,
        },
        shouldRefocusRef,
        activeFacets: selectedFacets,
        activePublicationYear: selectedPublicationYear,
        instanceIdScrollTo,
        isPageFromBackButton,
        updateState,
        setActiveFacets: setSelectedFacets,
        setActivePublicationYear: setSelectedPublicationYear,
        setSearchOption,
        setInstanceIdScrollTo,
        setIsPageFromBackButton,
        ...searchContext,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
